import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [reason, setReason] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setState("invalid");
      setReason("missing");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/validate`, { params: { token } });
        if (data.valid) {
          setState("valid");
          setEmail(data.email || "");
        } else {
          setState("invalid");
          setReason(data.reason || "invalid");
        }
      } catch {
        setState("invalid");
        setReason("invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setState("done");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-background" data-testid="reset-password-page">
      <div className="hidden lg:flex lg:col-span-2 flex-col justify-between p-10 border-r border-border bg-card">
        <img src="/brand/elevante-logo.png" alt="elevante.ai" className="h-9 w-auto self-start select-none" draggable={false} />
        <div className="max-w-md">
          <div className="eyebrow mb-3">Almost there</div>
          <h1 className="text-4xl leading-[1.05] tracking-tighter font-medium">
            Set your<br />new password.
          </h1>
          <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-sm">
            Pick something you&rsquo;ll actually remember. All other devices you&rsquo;re signed in on will be signed out.
          </p>
        </div>
        <div />
      </div>

      <div className="lg:col-span-3 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-sm space-y-6">
          {state === "checking" && (
            <div className="text-sm text-muted-foreground" data-testid="reset-checking">Checking your link…</div>
          )}

          {state === "invalid" && (
            <div className="space-y-4" data-testid="reset-invalid">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-destructive" strokeWidth={1.75} />
              </div>
              <div>
                <div className="eyebrow mb-2 text-destructive">Link problem</div>
                <h2 className="text-2xl tracking-tight font-medium">
                  {reason === "expired" && "This link expired."}
                  {reason === "used" && "This link was already used."}
                  {(reason === "invalid" || reason === "missing") && "This link is not valid."}
                </h2>
                <p className="mt-3 text-sm text-muted-foreground">
                  Reset links expire after 15 minutes and can only be used once. Request a new one and try again.
                </p>
              </div>
              <Link to="/forgot-password" data-testid="request-new-link">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Request a new link</Button>
              </Link>
              <div>
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
              </div>
            </div>
          )}

          {state === "valid" && (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <div className="eyebrow mb-2">Reset password</div>
                <h2 className="text-2xl tracking-tight font-medium">Set a new password</h2>
                {email && <p className="mt-2 text-sm text-muted-foreground">for <span className="mono">{email}</span></p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rp-new">New password</Label>
                <Input
                  id="rp-new"
                  type="password"
                  autoComplete="new-password"
                  data-testid="reset-new"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  minLength={6}
                />
                <p className="text-[11px] text-muted-foreground">At least 6 characters.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rp-confirm">Confirm password</Label>
                <Input
                  id="rp-confirm"
                  type="password"
                  autoComplete="new-password"
                  data-testid="reset-confirm"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
                {confirm && pw !== confirm && (
                  <p className="text-[11px] text-destructive" data-testid="reset-mismatch">Passwords don&rsquo;t match.</p>
                )}
              </div>
              {error && (
                <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="reset-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={saving || !pw || pw !== confirm}
                data-testid="reset-submit"
                className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {saving ? "Updating…" : "Set new password"}
              </Button>
            </form>
          )}

          {state === "done" && (
            <div className="space-y-4" data-testid="reset-done">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-primary" strokeWidth={1.75} />
              </div>
              <div>
                <div className="eyebrow mb-2">All set</div>
                <h2 className="text-2xl tracking-tight font-medium">Password updated.</h2>
                <p className="mt-3 text-sm text-muted-foreground">Taking you to sign in…</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
